import { useState, useEffect } from "react";
import api, { apiError, fmtDate, fmtTime, fmtDateTime } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LongTextarea, NoteView } from "@/components/LongText";
import { ShieldAlert, Download, Clock, FileText, UserX, Loader2, AlertTriangle, BookOpen, CheckCircle2, XCircle, RefreshCw, Server, Database } from "lucide-react";

const TABS = [
  { id: "richieste", label: "Richieste interessati" },
  { id: "breach", label: "Data breach" },
  { id: "conservazione", label: "Conservazione lead" },
  { id: "informativa", label: "Informativa privacy" },
  { id: "accessi", label: "Registro accessi" },
  { id: "backup", label: "Backup" },
];

export default function Privacy() {
  const [tab, setTab] = useState("richieste");
  return (
    <div className="fade-up max-w-5xl" data-testid="privacy-page">
      <SectionTitle sub="Gestione privacy e conformità GDPR: consensi, richieste degli interessati, violazioni dati, conservazione e registro accessi.">Privacy & GDPR</SectionTitle>
      <div className="flex flex-wrap gap-2 mt-5 mb-6 border-b border-[var(--border)] pb-3">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`privacy-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`text-sm font-medium px-3.5 py-1.5 rounded-lg transition-colors ${tab === t.id ? "bg-[#FC82ED] text-black" : "text-[var(--muted)] hover:text-[#0F0F12]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "richieste" && <Richieste />}
      {tab === "breach" && <Breach />}
      {tab === "conservazione" && <Conservazione />}
      {tab === "informativa" && <Informativa />}
      {tab === "accessi" && <Accessi />}
      {tab === "backup" && <Backup />}
    </div>
  );
}

function Informativa() {
  const { isDirezione } = useAuth();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ numero: "", data_entrata_vigore: "", testo: "", pdf_url: "", attiva: true });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/gdpr/informativa").then((r) => setRows(r.data.versioni || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.numero.trim()) return toast.error("Indica il numero di versione (es. v2).");
    setBusy(true);
    try { await api.post("/gdpr/informativa", f); toast.success("Nuova versione informativa registrata."); setF({ numero: "", data_entrata_vigore: "", testo: "", pdf_url: "", attiva: true }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const activate = async (id) => { try { await api.post(`/gdpr/informativa/${id}/attiva`); toast.success("Versione attivata."); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-5" data-testid="informativa-tab">
      {isDirezione && (
        <div className="card-immo p-5 space-y-3" data-testid="informativa-form">
          <div className="text-sm font-semibold flex items-center gap-2"><BookOpen size={16} className="text-[#FC82ED]" /> Nuova versione informativa</div>
          <div className="grid md:grid-cols-3 gap-3">
            <input data-testid="informativa-numero" placeholder="Numero versione (es. v2)" value={f.numero} onChange={(e) => setF({ ...f, numero: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
            <input data-testid="informativa-data" type="date" value={f.data_entrata_vigore} onChange={(e) => setF({ ...f, data_entrata_vigore: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
            <input data-testid="informativa-pdf" placeholder="URL PDF (opzionale)" value={f.pdf_url} onChange={(e) => setF({ ...f, pdf_url: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
          </div>
          <LongTextarea data-testid="informativa-testo" rows={4} placeholder="Testo dell'informativa privacy…" value={f.testo} onChange={(e) => setF({ ...f, testo: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="informativa-attiva" checked={f.attiva} onChange={(e) => setF({ ...f, attiva: e.target.checked })} className="accent-[#FC82ED] w-4 h-4" /> Imposta come versione attiva (disattiva le altre)</label>
          <button data-testid="informativa-save" onClick={create} disabled={busy} className="px-4 py-2 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm disabled:opacity-60">Registra versione</button>
        </div>
      )}
      <div className="card-immo divide-y divide-[var(--border)]" data-testid="informativa-list">
        <div className="p-4 text-sm font-medium">Versioni dell'informativa (non modificabili, consultabili sempre)</div>
        {rows.length === 0 && <div className="p-5 text-sm text-[var(--muted)]">Nessuna versione registrata.</div>}
        {rows.map((v) => (
          <div key={v.id} data-testid={`informativa-${v.id}`} className="p-4 flex items-start gap-4 text-sm">
            <span className="font-bold w-20 shrink-0">{v.numero}</span>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[var(--muted)]">In vigore dal {(v.data_entrata_vigore || "").slice(0, 10)} · creata da {v.created_by || "—"}</div>
              {v.testo && <NoteView text={v.testo} lines={3} className="text-xs text-slate-600 mt-1" />}
              {v.pdf_url && <a href={v.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-[#FC82ED] font-semibold">Apri PDF</a>}
            </div>
            {v.attiva ? (
              <span data-testid={`informativa-attiva-${v.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border text-emerald-600 bg-emerald-400/10 border-emerald-400/30"><CheckCircle2 size={12} /> Attiva</span>
            ) : isDirezione ? (
              <button data-testid={`informativa-activate-${v.id}`} onClick={() => activate(v.id)} className="text-xs font-semibold text-slate-600 hover:text-[#FC82ED]">Attiva</button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function Richieste() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ tipo: "accesso", interessato: "", email: "", note: "" });
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/gdpr/richieste").then((r) => setRows(r.data.richieste)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.interessato) return toast.error("Indica l'interessato.");
    setLoading(true);
    try { await api.post("/gdpr/richieste", f); toast.success("Richiesta registrata (scadenza 30 giorni)."); setF({ tipo: "accesso", interessato: "", email: "", note: "" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const close = async (id) => { try { await api.patch(`/gdpr/richieste/${id}`, { stato: "chiusa" }); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-5">
      <div className="card-immo p-5 grid md:grid-cols-5 gap-3 items-end" data-testid="richiesta-form">
        <select data-testid="richiesta-tipo" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm">
          {["accesso", "cancellazione", "rettifica", "opposizione", "portabilita"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input data-testid="richiesta-interessato" placeholder="Interessato" value={f.interessato} onChange={(e) => setF({ ...f, interessato: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
        <input data-testid="richiesta-email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
        <LongTextarea data-testid="richiesta-note" placeholder="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
        <button data-testid="richiesta-save" onClick={create} disabled={loading} className="px-4 py-2 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm disabled:opacity-60">Registra</button>
      </div>
      <div className="card-immo divide-y divide-[var(--border)]" data-testid="richieste-list">
        {rows.length === 0 && <div className="p-5 text-sm text-[var(--muted)]">Nessuna richiesta registrata.</div>}
        {rows.map((r) => (
          <div key={r.id} className="p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{r.interessato} · <span className="text-[#FC82ED]">{r.tipo}</span></div>
              <div className="text-xs text-[var(--muted)]">{r.email} · arrivo {fmtDate(r.data_arrivo)} · scadenza {fmtDate(r.scadenza)}</div>
            </div>
            {r.in_scadenza && <span className="text-[11px] font-semibold text-amber-600 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5">In scadenza</span>}
            <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${r.stato === "aperta" ? "text-emerald-600 bg-emerald-400/10" : "text-slate-500 bg-slate-100"}`}>{r.stato}</span>
            {r.stato === "aperta" && <button data-testid={`richiesta-close-${r.id}`} onClick={() => close(r.id)} className="text-xs font-medium text-[#FC82ED] hover:underline">Chiudi</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Breach() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ descrizione: "", dati_coinvolti: "", azioni: "" });
  const [loading, setLoading] = useState(false);
  const load = () => api.get("/gdpr/breach").then((r) => setRows(r.data.breach)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.descrizione) return toast.error("Descrivi la violazione.");
    setLoading(true);
    try { await api.post("/gdpr/breach", f); toast.success("Violazione registrata. Notifica al Garante entro 72 ore."); setF({ descrizione: "", dati_coinvolti: "", azioni: "" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  const markNotified = async (id) => { try { await api.patch(`/gdpr/breach/${id}`, { notificato_garante: true, stato: "notificato" }); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-5">
      <div className="card-immo p-5 space-y-3" data-testid="breach-form">
        <LongTextarea data-testid="breach-descrizione" placeholder="Descrizione della violazione" value={f.descrizione} onChange={(e) => setF({ ...f, descrizione: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
        <input data-testid="breach-dati" placeholder="Dati coinvolti" value={f.dati_coinvolti} onChange={(e) => setF({ ...f, dati_coinvolti: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
        <LongTextarea data-testid="breach-azioni" placeholder="Azioni intraprese" value={f.azioni} onChange={(e) => setF({ ...f, azioni: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
        <button data-testid="breach-save" onClick={create} disabled={loading} className="px-4 py-2 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm disabled:opacity-60">Registra violazione</button>
      </div>
      <div className="card-immo divide-y divide-[var(--border)]" data-testid="breach-list">
        {rows.length === 0 && <div className="p-5 text-sm text-[var(--muted)]">Nessuna violazione registrata.</div>}
        {rows.map((b) => (
          <div key={b.id} className="p-4 flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <NoteView text={b.descrizione} className="text-sm font-medium" />
              <div className="text-xs text-[var(--muted)]">Rilevata {fmtDate(b.data)} · dati: {b.dati_coinvolti || "—"} · scadenza 72h {fmtDate(b.scadenza_72h)}</div>
            </div>
            {b.scaduto_72h && <span className="text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 flex items-center gap-1"><AlertTriangle size={12} />72h superate</span>}
            {!b.notificato_garante
              ? <button data-testid={`breach-notify-${b.id}`} onClick={() => markNotified(b.id)} className="text-xs font-medium text-[#FC82ED] hover:underline whitespace-nowrap">Segna notificato</button>
              : <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-400/10 rounded-full px-2 py-0.5">Notificato</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Conservazione() {
  const [data, setData] = useState(null);
  const [mesi, setMesi] = useState(24);
  const [sel, setSel] = useState([]);
  const load = () => api.get("/gdpr/retention/candidati").then((r) => { setData(r.data); setMesi(r.data.retention_mesi); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const saveMesi = async () => { try { await api.post("/gdpr/retention/config", { retention_mesi: Number(mesi) }); toast.success("Periodo aggiornato."); load(); } catch (e) { toast.error(apiError(e)); } };
  const anonymize = async () => {
    if (sel.length === 0) return;
    if (!window.confirm(`Anonimizzare ${sel.length} lead? L'operazione è irreversibile.`)) return;
    try { const r = await api.post("/gdpr/retention/anonimizza", { ids: sel }); toast.success(`${r.data.anonimizzati} lead anonimizzati.`); setSel([]); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  return (
    <div className="space-y-5">
      <div className="card-immo p-5 flex items-end gap-3" data-testid="retention-config">
        <div>
          <label className="block text-[11px] uppercase tracking-wide text-[var(--muted)] mb-1">Periodo conservazione lead (mesi)</label>
          <input data-testid="retention-mesi" type="number" min={1} value={mesi} onChange={(e) => setMesi(e.target.value)} className="w-28 px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
        </div>
        <button data-testid="retention-save" onClick={saveMesi} className="px-4 py-2 rounded-lg border border-[var(--border)] text-sm font-medium hover:border-[#FC82ED]">Salva</button>
      </div>
      <div className="card-immo" data-testid="retention-list">
        <div className="p-4 flex items-center justify-between border-b border-[var(--border)]">
          <span className="text-sm font-medium flex items-center gap-2"><Clock size={16} className="text-[#FC82ED]" />{data ? data.count : 0} lead oltre la soglia, mai convertiti</span>
          {sel.length > 0 && <button data-testid="retention-anonymize" onClick={anonymize} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white flex items-center gap-1"><UserX size={14} />Anonimizza ({sel.length})</button>}
        </div>
        {(data?.candidati || []).length === 0 && <div className="p-5 text-sm text-[var(--muted)]">Nessun lead da anonimizzare.</div>}
        {(data?.candidati || []).map((c) => (
          <label key={c.id} className="p-4 flex items-center gap-3 border-b border-[var(--border)] last:border-0 cursor-pointer">
            <input type="checkbox" data-testid={`retention-cb-${c.id}`} checked={sel.includes(c.id)} onChange={() => toggle(c.id)} />
            <div className="flex-1 min-w-0"><div className="text-sm font-medium">{c.nome} {c.cognome}</div><div className="text-xs text-[var(--muted)]">stato {c.stato || "—"} · ultima attività {fmtDate(c.ultima_attivita)}</div></div>
          </label>
        ))}
      </div>
    </div>
  );
}

function Accessi() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/gdpr/accessi").then((r) => setRows(r.data.accessi)).catch(() => {}); }, []);
  return (
    <div className="card-immo divide-y divide-[var(--border)]" data-testid="accessi-list">
      <div className="p-4 text-sm font-medium flex items-center gap-2"><FileText size={16} className="text-[#FC82ED]" />Registro accessi e operazioni su dati personali (append-only)</div>
      {rows.length === 0 && <div className="p-5 text-sm text-[var(--muted)]">Nessun accesso registrato.</div>}
      {rows.map((a) => (
        <div key={a.id} className="p-3.5 flex items-center gap-4 text-sm">
          <span className="w-40 shrink-0 text-xs text-[var(--muted)]">{fmtDateTime(a.timestamp)}</span>
          <span className="font-medium w-36 shrink-0">{a.action}</span>
          <span className="flex-1 min-w-0 truncate text-[var(--muted)]">{a.user_name || a.user_id} {a.after?.ip ? `· ${a.after.ip}` : ""} {a.after?.file ? `· ${a.after.file}` : ""}</span>
        </div>
      ))}
    </div>
  );
}

const fmtBytes = (b) => {
  if (b == null) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let n = Number(b);
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i > 0 && n < 10 ? 1 : 0).replace(".", ",")} ${u[i]}`;
};

function Backup() {
  const { isDirezione } = useAuth();
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `immobilart_backup_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Backup scaricato.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="backup-tab">
      <div className="card-immo p-6 max-w-xl" data-testid="backup-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED]"><Download size={18} /></div>
          <h3 className="font-display font-bold text-base">Backup completo del database</h3>
        </div>
        <p className="text-sm text-[var(--muted)] mb-4 leading-relaxed">Scarica un dump JSON di tutte le collezioni + manifest con i conteggi. I file (documenti/foto) restano nell'object storage; il manifest ne riporta il numero.</p>
        <button data-testid="backup-download" onClick={download} disabled={busy} className="px-5 py-2.5 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Scarica backup completo
        </button>
      </div>
      {isDirezione && <BackupOffsite />}
    </div>
  );
}

function BackupOffsite() {
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const loadStatus = () => api.get("/gdpr/backup/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  const loadList = () => api.get("/gdpr/backup/list").then((r) => setBackups(r.data.backups || [])).catch(() => setBackups([]));
  useEffect(() => { Promise.all([loadStatus(), loadList()]).finally(() => setLoading(false)); }, []);
  const runNow = async () => {
    if (!window.confirm("Eseguire subito un backup cifrato verso Backblaze? L'operazione può richiedere qualche minuto.")) return;
    setRunning(true);
    try {
      const r = await api.post("/gdpr/backup/run", {}, { timeout: 180000 });
      toast.success(`Backup completato: ${fmtBytes(r.data.bytes)} caricati${r.data.pruned ? `, ${r.data.pruned} vecchi rimossi` : ""}.`);
      await Promise.all([loadStatus(), loadList()]);
    } catch (e) { toast.error(apiError(e)); } finally { setRunning(false); }
  };
  const configured = status?.configured;
  const ok = status?.last_success;
  const ko = status?.last_failed;
  const hoursSince = ok?.at ? (Date.now() - new Date(ok.at).getTime()) / 3600000 : null;
  let stale = null;
  if (hoursSince != null) {
    if (hoursSince > 72) stale = { cls: "border-red-300 bg-red-50 text-red-700", txt: `Attenzione: l'ultimo backup riuscito risale a più di 72 ore fa (${Math.floor(hoursSince)}h). Verifica il backup notturno e i Secret.` };
    else if (hoursSince > 36) stale = { cls: "border-amber-300 bg-amber-50 text-amber-800", txt: `L'ultimo backup riuscito risale a più di 36 ore fa (${Math.floor(hoursSince)}h).` };
  }
  return (
    <div className="card-immo p-6" data-testid="backup-offsite-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED]"><Server size={18} /></div>
          <div>
            <h3 className="font-display font-bold text-base">Backup off-site (Backblaze B2)</h3>
            <p className="text-xs text-[var(--muted)]">Dump cifrato (Fernet) caricato su storage esterno · conservazione 30 giorni · backup notturno automatico</p>
          </div>
        </div>
        {loading ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-[var(--muted)]"><Loader2 size={14} className="animate-spin" /> Verifica…</span>
        ) : configured ? (
          <span data-testid="backup-offsite-status" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border text-emerald-600 bg-emerald-400/10 border-emerald-400/30"><CheckCircle2 size={13} /> Configurato</span>
        ) : (
          <span data-testid="backup-offsite-status" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border text-red-600 bg-red-50 border-red-200"><XCircle size={13} /> Non configurato</span>
        )}
      </div>

      {!loading && !configured && (
        <div data-testid="backup-offsite-missing" className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
          Imposta in produzione i Secret mancanti: <b>{(status?.missing || []).join(", ") || "BACKUP_S3_*"}</b>. I valori non sono mai mostrati qui per sicurezza.
        </div>
      )}
      {!loading && stale && (
        <div data-testid="backup-offsite-stale" className={`mb-4 rounded-xl border p-3 text-xs font-medium flex items-center gap-2 ${stale.cls}`}>
          <AlertTriangle size={14} className="shrink-0" /> {stale.txt}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <div className="rounded-xl border border-[var(--border)] p-4" data-testid="backup-offsite-last-success">
          <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] mb-1 flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-500" /> Ultimo backup riuscito</div>
          {ok ? (
            <div className="text-sm">
              <div className="font-semibold">{fmtDateTime(ok.at)}</div>
              <div className="text-xs text-[var(--muted)] mt-0.5">{fmtBytes(ok.bytes)}{ok.records != null ? ` · ${ok.records} record` : ""}{ok.files != null ? ` · ${ok.files} file` : ""}{ok.pruned ? ` · ${ok.pruned} rimossi` : ""} · {ok.source === "manuale" ? "manuale" : "automatico"}</div>
            </div>
          ) : <div className="text-sm text-[var(--muted)]">Nessun backup riuscito finora.</div>}
        </div>
        <div className="rounded-xl border border-[var(--border)] p-4" data-testid="backup-offsite-last-failed">
          <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] mb-1 flex items-center gap-1.5"><AlertTriangle size={13} className="text-red-500" /> Ultimo tentativo fallito</div>
          {ko ? (
            <div className="text-sm">
              <div className="font-semibold">{fmtDateTime(ko.at)}</div>
              <div className="text-xs text-red-600 mt-0.5 break-words line-clamp-3">{ko.error || "Errore non specificato"}</div>
            </div>
          ) : <div className="text-sm text-[var(--muted)]">Nessun fallimento registrato.</div>}
        </div>
      </div>

      <button data-testid="backup-offsite-run" onClick={runNow} disabled={running || loading || !configured}
        className="px-5 py-2.5 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
        {running ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} {running ? "Backup in corso…" : "Esegui backup ora"}
      </button>
      <p className="text-[11px] text-[var(--muted)] mt-2">Massimo 3 esecuzioni manuali all'ora. Ogni esecuzione è registrata nel registro accessi.</p>

      <div className="mt-6" data-testid="backup-offsite-list">
        <div className="text-sm font-semibold flex items-center gap-2 mb-2"><Database size={15} className="text-[#FC82ED]" /> Backup presenti nel bucket {backups ? `(${backups.length})` : ""}</div>
        <div className="rounded-xl border border-[var(--border)] divide-y divide-[var(--border)] max-h-72 overflow-auto">
          {loading && <div className="p-4 text-sm text-[var(--muted)] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Caricamento…</div>}
          {!loading && (!backups || backups.length === 0) && <div className="p-4 text-sm text-[var(--muted)]">Nessun file di backup presente nel bucket.</div>}
          {!loading && (backups || []).map((b) => (
            <div key={b.key} data-testid={`backup-file-${b.name}`} className="p-3 flex items-center gap-3 text-sm">
              <FileText size={15} className="text-[var(--muted)] shrink-0" />
              <span className="flex-1 min-w-0 truncate font-mono text-xs">{b.name}</span>
              <span className="text-xs text-[var(--muted)] shrink-0 w-28 text-right hidden sm:block">{(b.records != null || b.files != null) ? `${b.records ?? "?"} rec · ${b.files ?? "?"} file` : "—"}</span>
              <span className="text-xs text-[var(--muted)] shrink-0 w-28 text-right">{fmtDateTime(b.at)}</span>
              <span className="text-xs font-semibold shrink-0 w-16 text-right">{fmtBytes(b.bytes)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
